import { useState } from 'react'
import styles from '../styles/pages/Appointments.module.scss'
import MembershipCredits from '../components/core/MembershipCredits'
import { useCancelAppointment, useUserAppointments } from '../hooks/useAppointments'
import AppointmentIcon from '../assets/icons/appointments.svg'
import TherapistIcon from '../assets/icons/massage-therapist.svg'
import MapPinIcon from '../assets/icons/map-pin-new.svg'
import GuestsIcon from '../assets/icons/guests.svg'
import ClockIcon from '../assets/icons/clock.svg'
import { DateTime } from 'luxon';
import Spinner from '../components/core/Spinner'
import { Link } from 'react-router-dom'
import Modal from '../components/core/Modal'
import PhoneIcon from '../assets/icons/phone.svg'
import EnvelopeIcon from '../assets/icons/envelope.svg'
import { useBoutiques } from '../hooks/useBoutiques'
import { useToast } from '../components/core/ToastManager'
import { useQueryClient } from '@tanstack/react-query'
import ErrorScreen from '../components/core/ErrorMessages'
import { useUserMemberships } from '../hooks/useUserProfile'
import { formatPhoneNumber } from '../utils/formatPhone'
import { AppointmentGroup } from '../types/appointments'

export const Appointments:React.FC = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('upcoming')
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentGroup | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { data: appointments, isLoading: isAppointmentsLoading, error: appointmentsError } = useUserAppointments()
  const { data: boutiques, isLoading: isBoutiquesLoading, error: boutiquesError } = useBoutiques({page: 1, perPage: 100})
  const { data: userMemberships, isLoading: isMembershipsLoading, error: membershipsError } = useUserMemberships(true)
  const { mutate: cancelAppointment, isPending: isCancellationPending } = useCancelAppointment(
    () => {
      setIsCancelModalOpen(false);
      setSelectedAppointment(null);
      addToast('Your appointment was successfully canceled.', 'success')
      queryClient.invalidateQueries({ queryKey: ['userAppointments'] })
    },
    (error) => {
      console.error('Failed to cancel appointment:', error);
      addToast(`Failed to cancel appointment: ${error.errorMessages[0]}`, 'error');
    })
    
  const handleCancel = () => {
    if (!selectedAppointment) return;
  
    if (selectedAppointment.invoiceId) {
      cancelAppointment({ 
        boutiqueId: selectedAppointment.boutiqueId, 
        invoiceId: selectedAppointment.invoiceId 
      });
    } else {
      addToast('Failed to cancel appointment: Invoice ID not found', 'error');
    }
  };

  function isCurrentDateBefore(selectedDateString: string) {
    const currentDate = DateTime.utc();
    const selectedDate = DateTime.fromISO(selectedDateString, { zone: 'utc' }); 
  
    return currentDate < selectedDate;
  }

  const handleManageAppointment = (appointment: AppointmentGroup | null) => {
    setSelectedAppointment(appointment);
  };
  
  const handleCloseManageModal = () => {
    setSelectedAppointment(null);
  };
  
  const handleProceedCancel = () => {
    setIsCancelModalOpen(true);
  };
  
  const getSelectedBoutique = () => {
    if (!selectedAppointment || !boutiques) return null;
    return boutiques.data.boutiques.find(boutique => boutique.id === selectedAppointment.boutiqueId);
  };
  
  const currentBoutique = getSelectedBoutique();
  
  if (isAppointmentsLoading || isBoutiquesLoading) return <Spinner />
  if (appointmentsError || boutiquesError) return <ErrorScreen errors={[appointmentsError ?? undefined, boutiquesError ?? undefined]} />

  return (
    <div className={styles.appointments}>
      <h1 className={styles.header}>My Appointments</h1>
      <div className={styles.navigation}>
        <div className={styles.tabs}>
          <button onClick={() => setActiveTab('upcoming')} className={`${styles.tabButton} ${activeTab === 'upcoming' ? styles.active : ''}`}>Upcoming</button>
          <button onClick={() => setActiveTab('past')} className={`${styles.tabButton} ${activeTab === 'past' ? styles.active : ''}`}>Past</button>
        </div>
      {userMemberships?.totalCredits ? <MembershipCredits name="Credits From All Memberships:" amount={userMemberships.totalCredits} /> : null}
      </div>
      {activeTab === 'upcoming' && (
        <div className={styles.appointmentsList}>
          {appointments?.futureAppointments
            .filter(a => a.services.length > 0)
            .map((appointment, index) => {
            const boutique = boutiques?.data.boutiques.find(boutique => boutique.id === appointment.boutiqueId)
            const service = appointment.services.find(service => !service.isInfrared)
            const infrared = appointment.services.find(service => service.isInfrared)
            const rescheduleLink = `/reschedule?` +
              `invoiceId=${appointment.invoiceId}` +
              `&service=${service?.serviceName}` +
              `&duration=${service?.serviceDuration}` +
              `&time=${appointment.time}` +
              `&date=${appointment.date}` +
              `&therapistId=${service?.therapistId}` +
              `&location=${appointment.boutiqueId}` +
              `&addOns=${service?.addOns.length}` +
              `&guests=${appointment.noOfGuests}` +
              `&serviceId=${service?.zenotiServiceId}` +
              `&serviceSequence=${service?.sequence}` +
              `&infraredId=${infrared?.zenotiServiceId}` +
              `&infraredSequence=${infrared?.sequence}`;
            const isCancellable = isCurrentDateBefore(appointment.cancellableUntil)

            return (
              <div className={styles.appointment} key={appointment.invoiceId + index}>
                <h2>{DateTime.fromISO(appointment.date).toFormat('MMMM dd')}</h2>
                <div className={styles.infoItem}>
                  <img src={ClockIcon} alt="clock" />
                  <p>{appointment.time}</p>
                </div>
                <div className={styles.infoItem}>
                  <img src={MapPinIcon} alt="Map pin" />
                  <p>{appointment.boutiqueName}</p>
                </div>
                {appointment.services.map((service => {
                  return (
                    <div key={service.zenotiAppointmentId} className={`${styles.infoItem} ${styles.serviceDetails}`}>
                    <img src={AppointmentIcon} alt="Service name" />
                    <div>
                      <p>{service.serviceName}</p>
                      {service.addOnPackages?.length > 0 && (
                              <p>
                                {service.addOnPackages.map((addOn, index) => (
                                  <span key={index}>
                                    + {addOn.name}
                                    {index < service.addOnPackages.length - 1 && ', '}
                                  </span>
                                ))}
                              </p>
                            )}
                    </div>
                  </div>
                  )
                }))}
                <div className={`${styles.infoItem} ${service?.hideTherapist ? styles.hideTherapist : ''}`}>
                  <img src={TherapistIcon} alt="Therapist" />
                  <p>Therapist, {service?.therapistName}</p>
                </div>
                { 
                  service?.manualManagementMessage ? 
                  <p className={styles.manualManagementMessage}>
                    {service?.manualManagementMessage}
                  </p> :
                  <button onClick={() => handleManageAppointment(appointment)} className="button inverted">
                    Manage Appointment
                  </button>
                }
                <Link target="_blank" to={`https://www.google.com/maps/search/?api=1&query=${boutique?.lat},${boutique?.lng}`} className="button__underline">DIRECTIONS</Link>

                <Modal onClose={handleCloseManageModal} isOpen={selectedAppointment?.invoiceId === appointment.invoiceId}>
                  <div className={styles.manageAppointmentModal}>
                    <h2>Manage this Appointment</h2>
                    <p>We respectfully request at least 12 hours notice if you need to cancel or rebook your massage appointment. We charge 50% of the reservation made to the credit card on file to compensate our staff for missed wages when there is a last minute cancellation.</p>
                    {process.env.REACT_APP_SHOW_RESCHEDULE === '1' && appointment.noOfGuests === 1 && <Link onClick={handleCloseManageModal} to={rescheduleLink} className={`button inverted`}>RESCHEDULE APPOINTMENT</Link>}
                    {isCancellable && <button onClick={() => handleProceedCancel()} className={`button__underline`}>CANCEL APPOINTMENT</button>}
                    <div className={styles.separator}></div>
                    <p>Contact The NOW Massage {boutique?.name} directly:</p>
                    {boutique?.phoneNumber && 
                    <div className={styles.contactItem}>
                      <img src={PhoneIcon} alt="Phone" />
                      <Link to={`tel:${boutique.phoneNumber}`}>{formatPhoneNumber(boutique.phoneNumber)}</Link>
                    </div>}
                    {boutique?.franchiseEmail && 
                    <div className={styles.contactItem}>
                      <img src={EnvelopeIcon} alt="Envelope" />
                      <Link to={`mailto: ${boutique.franchiseEmail}`}>{boutique.franchiseEmail}</Link>
                    </div>}
                  </div>
                </Modal>
              </div>
            )
          })}
          {appointments?.futureAppointments.length === 0 && (
            <div className={`${styles.appointment} ${styles.noAppointment}`}>
              <div className={styles.cardTop}>
                <h2>Nothing Booked</h2>
                <p>Book an appointment.</p>
              </div>
              <Link to="/book" className="button">Book Now</Link>
            </div>
          )}
        </div>
      )}
      <Modal onClose={() => setIsCancelModalOpen(false)} isOpen={isCancelModalOpen}>
        <div className={styles.cancelAppointmentModal}>
          <h2>Are you sure you want to cancel this appointment?</h2>
          <p>We respectfully request at least 12 hours notice if you need to cancel or rebook your massage appointment. We charge 50% of the reservation made to the credit card on file to compensate our staff for missed wages when there is a last minute cancellation.</p>
          <p>If you miss your appointment or arrive 15 minutes past your scheduled service start time, the service is automatically canceled and the full price of the scheduled service is charged to the credit card on file. To ensure a relaxing experience, and get the full benefits of the massage, we advise guests to arrive on time (or early) for all scheduled appointments.</p>
          <button onClick={handleCancel} className={`button`}>
            {isCancellationPending ? <Spinner size={20} /> : "Yes, CANCEL"}
          </button>
          <button onClick={() => setIsCancelModalOpen(false)} className={`button__underline`}>NO, KEEP</button>
          <div className={styles.separator}></div>
          <p>Contact The NOW Massage {currentBoutique?.name} directly:</p>
          {currentBoutique?.phoneNumber && 
          <div className={styles.contactItem}>
            <img src={PhoneIcon} alt="Phone" />
            <Link to={`tel:${currentBoutique.phoneNumber}`}>{formatPhoneNumber(currentBoutique.phoneNumber)}</Link>
          </div>}
          {currentBoutique?.franchiseEmail && 
          <div className={styles.contactItem}>
            <img src={EnvelopeIcon} alt="Envelope" />
            <Link to={`mailto: ${currentBoutique.franchiseEmail}`}>{currentBoutique.franchiseEmail}</Link>
          </div>}
        </div>
      </Modal>

      {activeTab === 'past' && (
        <div className={styles.pastAppointmentsList}>
          {appointments?.pastAppointments.map((appointment, index) => {
            const boutique = boutiques?.data.boutiques.find(b => b.id === appointment.boutiqueId);
            const therapistService = appointment.services.find(s => !s.isInfrared) || appointment.services[0];

            return (
              <div className={styles.pastAppointment} key={appointment.invoiceId + index}>
                <h2>{DateTime.fromISO(appointment.date).toFormat('MMMM dd')}, {appointment.time}</h2>
                <div className={styles.pastAppointmentCard}>
                  <div className={`${styles.col} ${styles.firstCol}`}>
                    <div className={styles.infoItem}>
                      <img src={MapPinIcon} alt="Map pin" />
                      <p>{appointment.boutiqueName}</p>
                    </div>
                    {therapistService && (
                      <div className={`${styles.infoItem} ${therapistService.hideTherapist ? styles.hideTherapist : ''}`}>
                        <img src={TherapistIcon} alt="Therapist" />
                        <p>Therapist, {therapistService.therapistName || 'Any'}</p>
                      </div>
                    )}
                    {appointment.noOfGuests > 1 && (
                      <div className={styles.infoItem}>
                        <img src={GuestsIcon} alt="Guests" />
                        <p>Me, {appointment.noOfGuests - 1} {appointment.noOfGuests - 1 === 1 ? 'Guest' : 'Guests'}</p>
                      </div>
                    )}
                  </div>
                  <div className={`${styles.col} ${styles.secondCol}`}>
                    {appointment.services.map((service, sIdx) => (
                      <div key={sIdx} className={styles.infoItem}>
                        <img src={AppointmentIcon} alt="Service name" />
                        <div>
                          <p>{service.serviceName}</p>
                          {service.addOnPackages.length > 0 && (
                            <ul className={styles.addOns}>
                              {service.addOnPackages.map((addOn, i) => (
                                <li key={i}>{addOn.name}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={`${styles.col} ${styles.thirdCol}`}>
                    <Link to="/book" className={`button ${appointment.noOfGuests > 1 || therapistService?.manualManagementMessage ? `disabled ${styles.bookAgainDisabled}` : ''}`}>Book again</Link>
                  </div>
                </div>
              </div>
            );
          })}
          {appointments?.pastAppointments.length === 0 && (
            <div className={`${styles.appointment} ${styles.noAppointment}`}>
              <div className={styles.cardTop}>
                <h2>Past Appointments</h2>
                <p>You don&apos;t have any past appointments.</p>
              </div>
              <Link to="/book" className="button">Book Now</Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Appointments
